import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { BellRing, ToggleLeft } from "lucide-react";
import { Button } from "@veorest/ui";
import { fetchAdminSettings, updateAdminSettings } from "@veorest/api-client";
import { SettingsCard } from "./SettingsCard";

/**
 * Features (Settings → Features) — only REAL controls:
 *  • console capability gates that actually hide/disable the actions they name
 *    (Reports CSV export, manual payment entry);
 *  • the wallet alert re-notify interval (consumed by the push engine, 176).
 */
export function FeaturesSettingsPanel() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["admin-settings"], queryFn: fetchAdminSettings });

  const [reportsExport, setReportsExport] = useState(true);
  const [manualPayments, setManualPayments] = useState(true);
  const [renotifyHours, setRenotifyHours] = useState("24");

  useEffect(() => {
    const s = settings.data?.settings;
    if (!s) return;
    const toggles = s.feature_toggles?.value as Record<string, boolean> | undefined;
    if (toggles) {
      setReportsExport(toggles.reports_export !== false);
      setManualPayments(toggles.manual_payments !== false);
    }
    const hours = s.wallet_alert_renotify_hours?.value;
    if (typeof hours === "number" || typeof hours === "string") setRenotifyHours(String(hours));
  }, [settings.data]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["admin-settings"] });

  const saveConsole = useMutation({
    mutationFn: () =>
      updateAdminSettings({
        feature_toggles: { reports_export: reportsExport, manual_payments: manualPayments },
      }),
    onSuccess: () => {
      toast.success("Console capabilities saved");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveAlerts = useMutation({
    mutationFn: () => {
      const n = Number(renotifyHours);
      if (!Number.isFinite(n) || n < 1 || n > 168) {
        throw new Error("Re-notify interval must be between 1 and 168 hours.");
      }
      return updateAdminSettings({ wallet_alert_renotify_hours: Math.round(n) });
    },
    onSuccess: () => {
      toast.success("Wallet alert policy saved");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <SettingsCard
        icon={ToggleLeft}
        title="Console capabilities"
        subtitle="These switches actually gate the console actions they name — not placeholders."
        actions={
          <Button className="min-h-[40px]" loading={saveConsole.isPending} onClick={() => saveConsole.mutate()}>
            Save
          </Button>
        }
        footnote="Retired: the legacy 'credit packages catalog' toggle (credit-monetization era) is no longer shown — nothing consumed it."
      >
        <label className="flex items-start gap-3 rounded-xl border border-[var(--border-subtle)] px-4 py-3">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={reportsExport}
            onChange={(e) => setReportsExport(e.target.checked)}
          />
          <span>
            <span className="block text-sm font-bold">Reports CSV export</span>
            <span className="block text-xs font-semibold text-[var(--text-muted)]">
              When off, the export buttons are hidden on the Reports page.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-3 rounded-xl border border-[var(--border-subtle)] px-4 py-3">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={manualPayments}
            onChange={(e) => setManualPayments(e.target.checked)}
          />
          <span>
            <span className="block text-sm font-bold">Manual payment entry</span>
            <span className="block text-xs font-semibold text-[var(--text-muted)]">
              When off, recording payments and marking them paid are hidden on the Payments page.
            </span>
          </span>
        </label>
      </SettingsCard>

      <SettingsCard
        icon={BellRing}
        title="Wallet alert re-notify"
        subtitle="While a wallet stays at or below its alert threshold, the low-balance push repeats on this interval."
        actions={
          <Button className="min-h-[40px]" loading={saveAlerts.isPending} onClick={() => saveAlerts.mutate()}>
            Save
          </Button>
        }
        footnote="The alert threshold itself is per wallet — set it on the client/partner wallet card."
      >
        <label className="block space-y-1">
          <span className="text-sm font-bold">Re-notify every (hours)</span>
          <input
            className="input-base max-w-[160px]"
            inputMode="numeric"
            value={renotifyHours}
            onChange={(e) => setRenotifyHours(e.target.value)}
          />
        </label>
      </SettingsCard>
    </div>
  );
}
